import { useQuery } from "@apollo/client";
import React, { useState } from "react";
import { LoaderDots } from "../../../middleware/loaders";
import { lang } from "../../../middleware/lang";
import { formatNumber } from "../../../middleware/common-functions";
import { HeaderTitle } from "../common/titles";
import { GET_REVIEWS, ReviewsResult, Review } from "./queries";

const PAGE_SIZE = 20;

const ReviewCard: React.FC<{ review: Review }> = ({ review }) => {
  const date = new Date(review.feedback_timestamp);
  const dateStr = date.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div className="d-flex align-items-center">
            <span
              className={`mr-3 ${review.liked ? "text-success" : "text-danger"}`}
              style={{ fontSize: "1.5rem" }}
            >
              <i
                className={`fas ${review.liked ? "fa-thumbs-up" : "fa-thumbs-down"}`}
              ></i>
            </span>
            <div>
              <h6 className="mb-0">
                {review.client
                  ? review.client.full_name
                  : lang("Cliente anónimo", "Anonymous customer")}
              </h6>
              {review.client && (
                <small className="text-muted">{review.client.phone}</small>
              )}
            </div>
          </div>
          <div className="text-right">
            <small className="text-muted">{dateStr}</small>
            <br />
            <small className="text-muted">
              ${formatNumber(review.total)}
            </small>
          </div>
        </div>
        {review.feedback && (
          <p className="mt-2 mb-0 text-dark">
            <i className="fas fa-quote-left text-muted mr-1 small"></i>
            {review.feedback}
          </p>
        )}
      </div>
    </div>
  );
};

const Reviews: React.FC = () => {
  const [skip, setSkip] = useState(0);
  const { data, loading } = useQuery<ReviewsResult>(GET_REVIEWS, {
    variables: { skip, take: PAGE_SIZE },
    fetchPolicy: "cache-and-network",
  });

  if (loading && !data) {
    return <LoaderDots />;
  }

  const result = data?.getReviews;
  const reviews = result?.reviews || [];
  const totalCount = result?.total_count || 0;
  const likedCount = result?.liked_count || 0;
  const dislikedCount = result?.disliked_count || 0;
  const approvalRate =
    totalCount > 0 ? Math.round((likedCount / totalCount) * 100) : 0;

  return (
    <div className="container py-4">
      <HeaderTitle
        title={lang("Reseñas de Clientes", "Customer Reviews")}
      />

      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 className="mb-0">{totalCount}</h3>
              <small className="text-muted">
                {lang("Total reseñas", "Total reviews")}
              </small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 className="mb-0 text-success">
                <i className="fas fa-thumbs-up mr-1"></i>
                {likedCount}
              </h3>
              <small className="text-muted">
                {lang("Positivas", "Positive")}
              </small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 className="mb-0 text-danger">
                <i className="fas fa-thumbs-down mr-1"></i>
                {dislikedCount}
              </h3>
              <small className="text-muted">
                {lang("Negativas", "Negative")}
              </small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h3 className="mb-0">{approvalRate}%</h3>
              <small className="text-muted">
                {lang("Aprobación", "Approval")}
              </small>
            </div>
          </div>
        </div>
      </div>

      {reviews.length === 0 ? (
        <div className="text-center text-muted py-5">
          <i className="far fa-comment-alt fa-3x mb-3 d-block"></i>
          <p>
            {lang(
              "Aún no tienes reseñas de clientes",
              "No customer reviews yet",
            )}
          </p>
        </div>
      ) : (
        <>
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
          <div className="d-flex justify-content-between mt-3">
            <button
              className="btn btn-outline-secondary"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            >
              <i className="fas fa-chevron-left mr-1"></i>
              {lang("Anterior", "Previous")}
            </button>
            <span className="text-muted align-self-center">
              {skip + 1}-{Math.min(skip + PAGE_SIZE, totalCount)}{" "}
              {lang("de", "of")} {totalCount}
            </span>
            <button
              className="btn btn-outline-secondary"
              disabled={skip + PAGE_SIZE >= totalCount}
              onClick={() => setSkip(skip + PAGE_SIZE)}
            >
              {lang("Siguiente", "Next")}
              <i className="fas fa-chevron-right ml-1"></i>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Reviews;
